import { useRef, useState } from "react";
import { GoogleMapView } from "src/components/common/googleMaps/GoogleMapView.tsx";
import { Coordinates } from "src/api/generated/common/address/coordinates.ts";
import {
	defaultProximity,
	toGoogleLatLng,
} from "src/components/common/googleMaps/googleMapsUtils.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faSave, faTrash } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useAsyncFetch } from "src/utils/async/asyncFetch.ts";
import { nullIfBlank } from "src/utils/strings.tsx";
import { useDebounce } from "src/utils/useDebounce.ts";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { GeocodingApi } from "src/api/generated/common/address/geocodingApi.ts";
import { GooglePlacePrediction } from "src/api/generated/common/address/googlePlacePrediction.ts";
import { GooglePlacesAutocompletePaper } from "src/components/common/googleMaps/GooglePlacesAutocompletePaper.tsx";
import {
	AavoAdvancedMarker,
	createAdvancedMarker,
} from "src/components/common/googleMaps/googleMapsMarkers.ts";

export interface TaskLocation {
	latitude: number;
	longitude: number;
}

export interface TaskLocationPickerDialogProps {
	initialLocation?: TaskLocation;
	onCompleted: (location: TaskLocation | null) => Promise<void>;
}

interface FetchSuggestionsParams {
	input: string;
}

export const TaskLocationPickerDialog = ({
	initialLocation,
	onCompleted,
}: TaskLocationPickerDialogProps) => {
	const initialCoordinates: Coordinates | undefined =
		initialLocation != null
			? { lat: initialLocation.latitude, lng: initialLocation.longitude }
			: undefined;

	const mapRef = useRef<google.maps.Map | null>(null);
	const markerRef = useRef<AavoAdvancedMarker | null>(null);
	const { logErrorAndShowOnDialog } = useErrorDialog();
	const debounceInput = useDebounce();

	const [coordinates, setCoordinates] = useState<Coordinates | undefined>(initialCoordinates);
	const [searchInput, setSearchInput] = useState("");
	const [searchValue, setSearchValue] = useState<string | GooglePlacePrediction | null>(null);

	const [optionsAsync, fetchOptions] = useAsyncFetch<GooglePlacePrediction[], FetchSuggestionsParams>(
		async (params) => {
			try {
				const input = params?.input ?? searchInput;
				if (nullIfBlank(input) == null) return [];

				return await GeocodingApi.searchGooglePlaces({
					input: input,
					language: i18n.language,
				});
			} catch (e) {
				logErrorAndShowOnDialog(e);
				return [];
			}
		},
		{
			fetchOnMount: false,
		},
	);

	return (
		<Box
			sx={{
				flex: 1,
				position: "relative",
				display: "flex",
			}}
		>
			<GoogleMapView
				ref={mapRef}
				center={initialCoordinates ?? defaultProximity}
				options={
					initialCoordinates != null ?
						{
							zoom: 14,
						}
					:	undefined
				}
				onLoaded={onMapLoaded}
			/>
			<Box
				sx={{
					position: "absolute",
					top: 8,
					left: 8,
					right: 8,
					zIndex: 1,
					maxWidth: 350,
				}}
			>
				<Autocomplete
					options={optionsAsync.data ?? []}
					value={searchValue}
					inputValue={searchInput}
					filterOptions={(x) => x}
					getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
					freeSolo
					slots={{
						paper: GooglePlacesAutocompletePaper,
					}}
					onChange={async (_, value) => {
						setSearchValue(value);
						if (typeof value === "string" || value == null) return;
						await placeSearchResult(value);
					}}
					onInputChange={(_, value) => {
						setSearchInput(value);
						debounceInput(200, async () => {
							await fetchOptions({ input: value });
						});
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							size={"small"}
							placeholder={i18n.t("search")}
							slotProps={{
								...params.slotProps,
								input: {
									...params.slotProps.input,
									endAdornment: (
										<>
											{optionsAsync.loading && <CircularProgress size={20} color={"inherit"} />}
											{params.slotProps.input.endAdornment}
										</>
									),
								},
							}}
							sx={{
								backgroundColor: "background.paper",
								boxShadow: 2,
							}}
						/>
					)}
					renderOption={(props, option) => {
						const { key, ...optionProps } = props;
						return (
							<li key={key} {...optionProps}>
								<HorizontalBox sx={{ gap: 1.5, marginY: 0.5, alignItems: "center" }}>
									<FontAwesomeIcon icon={faLocationDot} />
									<Typography>{option.description}</Typography>
								</HorizontalBox>
							</li>
						);
					}}
				/>
			</Box>
			<Box
				sx={{
					position: "absolute",
					bottom: 10,
					right: 50,
					zIndex: 1,
					display: "flex",
					gap: 1,
				}}
			>
				<AsyncButton
					label={i18n.t("clear_location")}
					icon={faTrash}
					variant={"contained"}
					color={"secondary"}
					size={"small"}
					sx={{
						boxShadow: 2,
					}}
					onClick={handleClearLocation}
				/>
				<AsyncButton
					label={i18n.t("save_location")}
					icon={faSave}
					variant={"contained"}
					disabled={coordinates == null}
					size={"small"}
					sx={{
						boxShadow: 2,
					}}
					onClick={handleSave}
				/>
			</Box>
		</Box>
	);

	function onMapLoaded(map: google.maps.Map) {
		if (initialCoordinates != null) {
			void placeMarker({
				lng: initialCoordinates.lng,
				lat: initialCoordinates.lat,
			});
		}

		map.addListener("click", (e: google.maps.MapMouseEvent) => {
			if (e.latLng == null) return;
			void placeMarker({ lng: e.latLng.lng(), lat: e.latLng.lat() });
		});
	}

	async function handleSave() {
		if (coordinates == null) return;
		await onCompleted({
			latitude: coordinates.lat,
			longitude: coordinates.lng,
		});
	}

	async function handleClearLocation() {
		removeMarker();
		await onCompleted(null);
	}

	async function placeMarker(lngLat: { lng: number; lat: number }) {
		const map = mapRef.current;
		if (map == null) return;

		if (markerRef.current != null) {
			markerRef.current.setPosition(lngLat);
		} else {
			markerRef.current = await createAdvancedMarker({
				map: map,
				position: lngLat,
				gmpDraggable: true,
			});

			markerRef.current.googleMarker.addEventListener("gmp-dragend", () => {
				const newLngLat = markerRef.current!.getPosition();
				if (newLngLat == null) return;
				setCoordinates({ lat: newLngLat.lat(), lng: newLngLat.lng() });
			});
		}

		setCoordinates({ lat: lngLat.lat, lng: lngLat.lng });
	}

	async function placeSearchResult(suggestion: GooglePlacePrediction) {
		const result = await GeocodingApi.geocodeGooglePlaceId({
			placeId: suggestion.placeId,
			language: i18n.language,
		});
		const location = result?.location;
		if (location == null) return;

		const lngLat = { lng: location.lng, lat: location.lat };
		await placeMarker(lngLat);
		mapRef.current?.setCenter(toGoogleLatLng(lngLat));
		mapRef.current?.setZoom(14);
	}

	function removeMarker() {
		if (markerRef.current != null) {
			markerRef.current.remove();
			markerRef.current = null;
		}
		setCoordinates(undefined);
	}
};
